import {useSelector} from "react-redux";
import {useParams} from "react-router";
import {css, Theme} from "@emotion/react";
import {capitalizeFirst} from "@rw/nodash";
import {pluralize} from "@rw/string_utils";

import {IStore} from "../../../app/reducers/hybrid_reducer";
import {getVoivodeshipName, Voivodeship} from "../../../app/types/locationTypes";
import {parseOfferListPath, toModelSlug} from "../url_utils/parse_offer_list_path";
import {formatMakeDisplayName} from "../utils/format_make_display_name";
import {formatModelDisplayName} from "../utils/format_model_display_name";

/** Build list path with optional model segment — avoids path-to-regexp dropping params. */
const buildListHref = (parts: {
    offerType?: string;
    type?: string;
    model?: string;
    location?: string;
    subLocation?: string;
}): string => {
    const offerType = parts.offerType || "osobowe";
    const type = parts.type ? String(parts.type).toLowerCase() : "";
    const model = parts.model ? toModelSlug(parts.model) : "";
    const location = parts.location ? String(parts.location).toLowerCase() : "";
    const subLocation = parts.subLocation ? String(parts.subLocation).toLowerCase() : "";

    const segments = [offerType, type, model, location, subLocation].filter(Boolean);
    return `/${segments.join("/")}/`;
};

export const SeoLocationLinks = () => {
    const topRegions = useSelector((store: IStore) => store.carList.carsTopLocations);
    const locationRegions = useSelector((store: IStore) => store.carList.carsLocations);
    const latestQuery = useSelector((store: IStore) => store.carList.latestQuery);
    const routeParams = useParams<{
        type?: string;
        location?: string;
        subLocation?: string;
        subSubLocation?: string;
    }>();
    const parsed = parseOfferListPath(routeParams || {});
    const pluralOffer = pluralize(["oferta", "oferty", "ofert"]);

    const makeSlug = (parsed.type || latestQuery.type || "").toString().toLowerCase() || undefined;
    const makeType = formatMakeDisplayName(makeSlug || latestQuery.type);
    const modelSlug = parsed.model || (latestQuery as {model?: string}).model || undefined;
    const modelLabel = formatModelDisplayName(modelSlug);
    const makeModelLabel = [makeType, modelLabel].filter(Boolean).join(" ");
    const makeTitleSuffix = makeModelLabel ? ` - ${makeModelLabel}` : "";
    const offerType = (latestQuery.offer_type as string) || "osobowe";

    return (
        <div>
            {locationRegions && (
                <div css={containerStyle}>
                    <h2 css={titleStyle}>Sugerowane oferty z pobliskich lokalizacji</h2>
                    <div css={singleRegionContainer}>
                        <h3 css={regionTitleStyle}>{capitalizeFirst(getVoivodeshipName(locationRegions.region as Voivodeship))}</h3>
                        <ul css={listStyle}>
                            {locationRegions.cities.map((city) => {
                                const offerLinkTitle = `Samochody osobowe${makeModelLabel ? ` ${makeModelLabel} ` : " "}- ${city.city} (${city.offer_count} ${pluralOffer(city.offer_count)})`;
                                const href = buildListHref({
                                    offerType,
                                    type: makeSlug,
                                    model: modelSlug,
                                    location: locationRegions.region.toLowerCase(),
                                    subLocation: city.citySlug
                                });
                                return (
                                    <li key={city.citySlug}>
                                        <a
                                            href={href}
                                            css={linkStyle}
                                            title={`Oferty w mieście ${city.city}${makeTitleSuffix}`}
                                        >
                                            {offerLinkTitle}
                                        </a>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </div>
            )}
            {topRegions && (
                <div css={containerStyle}>
                    <h2 css={titleStyle}>Sugerowane oferty z pobliskich lokalizacji</h2>
                    <div css={regionContainer}>
                        {Object.entries(topRegions).map(([region, cities]) => (
                            <div key={region} css={regionSectionStyle}>
                                <h3 css={regionTitleStyle}>
                                    <a
                                        href={buildListHref({
                                            offerType,
                                            type: makeSlug,
                                            model: modelSlug,
                                            location: region.toLowerCase()
                                        })}
                                    >
                                        {capitalizeFirst(getVoivodeshipName(region as Voivodeship))}
                                    </a>
                                </h3>
                                <ul css={listStyle}>
                                    {cities.map((city) => {
                                        return (
                                            <li key={city.citySlug}>
                                                <a
                                                    href={buildListHref({
                                                        offerType,
                                                        type: makeSlug,
                                                        model: modelSlug,
                                                        location: region.toLowerCase(),
                                                        subLocation: city.citySlug
                                                    })}
                                                    css={linkStyle}
                                                    title={`Oferty w mieście ${city.city}${makeTitleSuffix}`}
                                                >
                                                    {city.city} ({city.offer_count} {pluralOffer(city.offer_count)})
                                                </a>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const containerStyle = css`
    padding: 24px;
    display: flex;
    flex-direction: column;
`;
const singleRegionContainer = (theme: Theme) => css`
    display: flex;
    gap: 1rem;
    flex-direction: column;
`;

const regionContainer = (theme: Theme) => css`
    display: flex;
    flex-wrap: wrap;
    gap: 32px;

    flex: 1 1 100%;

    @media (min-width: ${theme.breakpoints.screen_md}) {
        flex: 1 1 23%;
        justify-content: space-between;
    }
`;

const titleStyle = css`
    font-size: 2.5rem;
    font-weight: 600;
    color: #333;
`;

const regionSectionStyle = css`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const regionTitleStyle = (theme: Theme) => css`
    font-size: 2rem;
    font-weight: 600;
    color: #333;
    a {
        color: inherit;
        text-decoration: none;
        &:hover {
            text-decoration: underline;
        }
    }
`;

const listStyle = css`
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const linkStyle = (theme: Theme) => css`
    color: ${theme.colors?.primary || "#2e79bd"};
    text-decoration: underline;
    font-size: 1.4rem;
    &:hover {
        opacity: 0.85;
    }
`;
